import React, { useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import { motion } from 'framer-motion';
import { fadeIn } from '../../../lib/motion';
import { getSignedUrl } from '../../../services/uploads';
import ModuleViewContainer from './ModuleViewContainer';

const ALLOWED_TAGS = [
  'p', 'br', 'strong', 'em', 'u', 's', 'h1', 'h2', 'h3', 'h4',
  'a', 'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'hr', 'img',
];
const ALLOWED_ATTR = [
  'href', 'target', 'rel', 'class',
  // Images stored by the editor: `data-upload-key` is the durable S3 key,
  // resolved into a presigned URL on mount (see RichTextBody).
  'src', 'alt', 'data-upload-key',
];

export interface RichTextViewProps {
  item: { content?: string; title?: string };
  mode: 'learner' | 'preview';
}

/**
 * Renders sanitized rich text and post-resolves any `<img data-upload-key>`
 * placeholders into fresh presigned URLs (signed URLs expire ~1h, so we
 * never trust the `src` baked in at edit time).
 */
const RichTextBody: React.FC<{ html: string; className?: string }> = ({
  html,
  className,
}) => {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const imgs = el.querySelectorAll<HTMLImageElement>('img[data-upload-key]');
    if (imgs.length === 0) return;
    let cancelled = false;
    imgs.forEach(async (img) => {
      const key = img.dataset.uploadKey;
      if (!key) return;
      try {
        const result = await getSignedUrl({ key });
        if (!cancelled) img.src = result.url;
      } catch {
        // best-effort — leave the (likely stale) embedded src in place
      }
    });
    return () => {
      cancelled = true;
    };
  }, [html]);

  return (
    <div
      ref={ref}
      className={className}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

const PROSE_CLASSES = [
  'max-w-none text-text-primary',
  '[&_h1]:text-3xl [&_h1]:font-bold [&_h1]:mt-6 [&_h1]:mb-3 [&_h1]:first:mt-0',
  '[&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:mt-5 [&_h2]:mb-2',
  '[&_h3]:text-xl [&_h3]:font-semibold [&_h3]:mt-4 [&_h3]:mb-2',
  '[&_h4]:text-lg [&_h4]:font-semibold [&_h4]:mt-3 [&_h4]:mb-1.5',
  '[&_p]:my-2 [&_p]:leading-relaxed [&_p:empty]:hidden',
  '[&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-2',
  '[&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-2',
  '[&_li]:my-1',
  '[&_a]:text-primary-600 dark:[&_a]:text-primary-350 [&_a]:underline hover:[&_a]:text-primary-700 dark:hover:[&_a]:text-primary-300',
  '[&_strong]:font-semibold [&_em]:italic [&_u]:underline [&_s]:line-through',
  '[&_blockquote]:border-l-4 [&_blockquote]:border-border-secondary [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-text-secondary [&_blockquote]:my-3',
  '[&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-sm [&_code]:font-mono [&_code]:bg-neutral-100 dark:[&_code]:bg-neutral-800',
  '[&_pre]:my-3 [&_pre]:p-3 [&_pre]:rounded-lg [&_pre]:overflow-x-auto [&_pre]:bg-neutral-100 dark:[&_pre]:bg-neutral-800',
  '[&_hr]:my-6 [&_hr]:border-border-secondary',
  '[&_img]:max-w-full [&_img]:rounded-lg [&_img]:my-3 [&_img]:mx-auto [&_img]:block',
].join(' ');

const RichTextView: React.FC<RichTextViewProps> = ({ item, mode }) => {
  const html = DOMPurify.sanitize(item.content ?? '', {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
  });

  if (mode === 'learner') {
    return (
      <motion.div {...fadeIn} className="w-full flex-1 flex flex-col min-h-0">
        <ModuleViewContainer padding="px-4 pb-4">
          {item.title && (
            <div className="px-5 sm:px-6 pt-5 sm:pt-6 pb-3">
              <h2 className="text-lg sm:text-xl font-bold text-text-primary leading-tight">
                {item.title}
              </h2>
            </div>
          )}
          <div className="flex-1 flex flex-col min-h-0 px-3 sm:px-4 pb-3 sm:pb-4">
            <div className="flex-1 min-h-0 rounded-2xl bg-surface-primary shadow-[0_4px_10px_rgba(0,0,0,0.1)] p-4 sm:p-6 overflow-y-auto scrollbar-thin">
              <RichTextBody html={html} className={PROSE_CLASSES} />
            </div>
          </div>
        </ModuleViewContainer>
      </motion.div>
    );
  }

  // Preview mode — render bare so the Course Preview Overlay's own chrome
  // isn't doubled.
  return (
    <motion.div {...fadeIn} className="w-full">
      <RichTextBody html={html} className={PROSE_CLASSES} />
    </motion.div>
  );
};

export default RichTextView;
